import React, { useState, useEffect, useCallback } from "react";
import * as api from "../userApi";
import { useAuth } from "../../auth/AuthContext";
import "./users.css";

const EMPLOYEE_ROLES = ["manager", "marketing_executive", "viewer"];
const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  manager: "Manager",
  marketing_executive: "Marketing Exec",
  viewer: "Viewer",
  customer: "Customer",
};
const ROLE_COLOR = {
  super_admin: "var(--accent2)",
  admin: "var(--accent)",
  manager: "var(--green)",
  marketing_executive: "var(--google)",
  viewer: "var(--text2)",
  customer: "var(--amber)",
};

function CreateModal({ roles, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: roles[0] || "customer" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      await api.createUser(form);
      onCreated();
    } catch (err) {
      const res = err.response?.data;
      setError(res?.message || err.message);
      setFieldErrors(res?.errors || {});
      setBusy(false);
    }
  };

  return (
    <div className="proj-modal-backdrop" onClick={onClose}>
      <div className="proj-modal" onClick={(e) => e.stopPropagation()}>
        <h2>New user</h2>
        <form onSubmit={submit} className="proj-form">
          <label className="proj-field">
            <span>Name *</span>
            <input value={form.name} onChange={set("name")} autoFocus />
            {fieldErrors.name && <em className="proj-err">{fieldErrors.name[0]}</em>}
          </label>
          <label className="proj-field">
            <span>Email *</span>
            <input type="email" value={form.email} onChange={set("email")} />
            {fieldErrors.email && <em className="proj-err">{fieldErrors.email[0]}</em>}
          </label>
          <label className="proj-field">
            <span>Temporary password *</span>
            <input type="text" value={form.password} onChange={set("password")} placeholder="Min 8, upper/lower/number/special" />
            {fieldErrors.password && <em className="proj-err">{fieldErrors.password[0]}</em>}
          </label>
          <label className="proj-field">
            <span>Role *</span>
            <select className="proj-select" value={form.role} onChange={set("role")}>
              {roles.map((r) => (
                <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>
              ))}
            </select>
          </label>
          {error && <div className="proj-alert">{error}</div>}
          <div className="proj-modal-actions">
            <button type="button" className="proj-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="proj-btn proj-btn-primary" disabled={busy}>
              {busy ? "Creating…" : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignModal({ employee, onClose, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([api.getCustomers(), api.getAssignments(employee.id)])
      .then(([all, assigned]) => {
        setCustomers(all);
        setSelected(new Set(assigned.map((c) => c.id)));
      })
      .catch((e) => setError(e.response?.data?.message || e.message));
  }, [employee.id]);

  const toggle = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.setAssignments(employee.id, [...selected]);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="proj-modal-backdrop" onClick={onClose}>
      <div className="proj-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Assign customers</h2>
        <p className="proj-muted">
          {employee.name} ({ROLE_LABEL[employee.role]}) will see the projects of the selected customers.
        </p>
        {error && <div className="proj-alert">{error}</div>}
        <div className="assign-list">
          {customers.length === 0 && <p className="proj-muted">No customers yet.</p>}
          {customers.map((c) => (
            <label key={c.id} className={`assign-item ${selected.has(c.id) ? "sel" : ""}`}>
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
              <span>
                <strong>{c.name}</strong>
                <span className="proj-sub"> {c.email}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="proj-modal-actions">
          <button className="proj-btn" onClick={onClose}>Cancel</button>
          <button className="proj-btn proj-btn-primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save assignments"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [assignFor, setAssignFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listUsers({ search: search || undefined, role: roleFilter || undefined, limit: 100 });
      setUsers(res.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.getRoles().then(setRoles).catch(() => {}); }, []);

  const changeRole = async (u, role) => {
    try {
      await api.updateUser(u.id, { role });
      load();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.updateUser(u.id, { isActive: !u.isActive });
      load();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const canEdit = (u) => u.id !== user.id && roles.includes(u.role);

  return (
    <div className="proj-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="proj-muted">Create staff &amp; customers, assign roles and customers</p>
        </div>
        <button className="proj-btn proj-btn-primary" onClick={() => setShowCreate(true)}>+ New User</button>
      </div>

      <div className="proj-body">
        <div className="proj-toolbar">
          <form className="proj-search" onSubmit={(e) => { e.preventDefault(); load(); }}>
            <input placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <button className="proj-btn" type="submit">Search</button>
          </form>
          <select className="proj-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">All roles</option>
            {Object.keys(ROLE_LABEL).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        </div>

        {loading && <div className="proj-center proj-muted">Loading users…</div>}
        {error && <div className="proj-alert">{error}</div>}

        {!loading && !error && (
          <div className="proj-panel" style={{ overflowX: "auto" }}>
            <table className="user-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Projects</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={u.isActive ? "" : "row-inactive"}>
                    <td>{u.name}{u.id === user.id && <span className="proj-sub"> (you)</span>}</td>
                    <td className="proj-muted">{u.email}</td>
                    <td>
                      {canEdit(u) ? (
                        <select className="role-select" value={u.role} onChange={(e) => changeRole(u, e.target.value)}>
                          {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                        </select>
                      ) : (
                        <span className="proj-badge" style={{ color: ROLE_COLOR[u.role], borderColor: ROLE_COLOR[u.role] }}>
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      )}
                    </td>
                    <td className="proj-muted">{u.projectCount ?? 0}</td>
                    <td>{u.isActive ? <span style={{ color: "var(--green)" }}>Active</span> : <span className="proj-muted">Inactive</span>}</td>
                    <td>
                      <div className="user-actions">
                        {EMPLOYEE_ROLES.includes(u.role) && (
                          <button className="proj-link" onClick={() => setAssignFor(u)}>Assign customers</button>
                        )}
                        {canEdit(u) && (
                          <button className="proj-link" onClick={() => toggleActive(u)}>
                            {u.isActive ? "Deactivate" : "Activate"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateModal
          roles={roles}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {assignFor && (
        <AssignModal
          employee={assignFor}
          onClose={() => setAssignFor(null)}
          onSaved={() => setAssignFor(null)}
        />
      )}
    </div>
  );
}
